// File: src/components/Navbar.js

import React, { useState, useContext } from 'react'
import { NavLink } from 'react-router-dom'
import logo from '../assets/images/nesim-logo.jpg'
import {
  FaHome,
  FaChevronRight,
  FaChevronDown,
  FaUsersCog,
  FaSignOutAlt,
  FaSitemap,
  FaTasks,
  FaChartLine,
  FaBook,
  FaUsers,
  FaProjectDiagram,
  FaComments,
  FaDollarSign,
  FaUserCircle,
  FaBug,
  FaQuestionCircle,
  FaCog,
  FaServer,
  FaChartBar,
  FaRobot,
  FaBoxes,
  FaAddressBook,
} from 'react-icons/fa'
import { AuthContext } from '../context/AuthContext'
import { useChat } from '../context/ChatContext'
import { prefetchCostCardsList } from '../utils/costCardsList'
import './Navbar.css'
import SupportFormModal from './SupportFormModal';

// Business Workflows subpages
const workflowsSubItems = [
  { label: 'Technical Sales', path: '/workflows/technical-sales' },
  { label: 'Store Management', path: '/workflows/store-management' },
  { label: 'Sales', path: '/workflows/sales' },
  { label: 'Import Workflow', path: '/workflows/import' }
]

// Company Strategy subpages
const companyStrategySubItems = [
  { label: 'Company Strategy', path: '/company-strategy/company-strategy' },
  { label: 'Business Continuity Plan', path: '/company-strategy/nesim-business-continuity-plan-final' }
]

// SOP subpages
const sopSubItems = [
  { label: 'Data Management SOP', path: '/company-strategy/nesim-data-management-sop' },
  { label: 'Procurement SOP', path: '/company-strategy/nesim-procurement-sop-final' },
  { label: 'Risk and Compliance SOP', path: '/company-strategy/nesim-risk-and-compliance-sops-final' }
]

// HR subpages
const hrSubItems = [
  { label: 'HR Management System', path: '/hr', icon: <FaUsers /> },
  {
    label: 'Job Descriptions and Levels',
    subItems: [
      { label: 'Head Of Supply Chain', path: '/human-resources/job-descriptions/head-of-supply-chain' },
      { label: 'Office Administrator', path: '/human-resources/job-descriptions/office-administrator' },
      { label: 'Technical Officer', path: '/human-resources/job-descriptions/technical-officer' },
      { label: 'Managing Director', path: '/human-resources/job-descriptions/managing-director' },
      { label: 'Accountant', path: '/human-resources/job-descriptions/accountant' },
      { label: 'Business Development Manager', path: '/human-resources/job-descriptions/business-development-manager' },
      { label: 'Finance Manager', path: '/human-resources/job-descriptions/finance-manager' }
    ]
  },
  { label: 'Annual Performance Reviews', path: '/human-resources/employees-reviews' },
  {
    label: 'Interview Processes',
    subItems: [
      { label: 'Accountant', path: '/human-resources/interview-processes/accountant' },
      { label: 'Head of Supply Chain', path: '/human-resources/interview-processes/head-of-supply-chain' },
      { label: 'Office Administrator', path: '/human-resources/interview-processes/office-administrator' },
      { label: 'Technical Officer', path: '/human-resources/interview-processes/technical-officer' },
      { label: 'Managing Director', path: '/human-resources/interview-processes/managing-director' },
      { label: 'Business Development Manager', path: '/interview-processes/business-development-manager' }
    ]
  }
]

// Automation subpages - removed, now using standalone Workflow Automations

// Main nav items
const navItems = [
  { label: 'Organisation Structure', path: '/org-structure', icon: <FaSitemap /> },
  { label: 'Business Workflows', icon: <FaTasks />, subItems: workflowsSubItems },
  { label: 'Workflow Automations', path: '/intranet/automation/workflows', icon: <FaRobot /> },
  { label: 'Company Strategy', icon: <FaChartLine />, subItems: companyStrategySubItems },
  { label: 'Standard Operating Procedures', icon: <FaBook />, subItems: sopSubItems },
  { label: 'Human Resources', icon: <FaUsers />, subItems: hrSubItems },
  { label: 'OKRs', icon: <FaProjectDiagram /> },
  { label: 'Team Chat', path: '/chat', icon: <FaComments /> },
  { label: 'Communication SLAs', path: '/slas', icon: <FaComments /> },
  { label: 'Contacts', path: '/contacts', icon: <FaAddressBook /> },
  { label: 'Cost Cards', path: '/costcards', icon: <FaDollarSign /> }
]

// Section groupings for sidebar
const sidebarSections = [
  {
    label: 'Core Structure',
    items: [
      { label: 'Home', path: '/', icon: <FaHome /> },
      { label: 'Organisation Structure', path: '/org-structure', icon: <FaSitemap /> },
      { label: 'Business Workflows', icon: <FaTasks />, subItems: workflowsSubItems },
      { label: 'Workflow Automations', path: '/intranet/automation/workflows', icon: <FaRobot /> },
      { label: 'Company Strategy', icon: <FaChartLine />, subItems: companyStrategySubItems },
      { label: 'Standard Operating Procedures', icon: <FaBook />, subItems: sopSubItems },
      { label: 'Human Resources', icon: <FaUsers />, subItems: hrSubItems },
    ]
  },
  {
    label: 'Goals & Metrics',
    items: [
      { label: 'OKRs', icon: <FaProjectDiagram /> },
      { label: 'Cost Cards', path: '/costcards', icon: <FaDollarSign /> },
      { label: 'Inventory Management', path: '/inventory', icon: <FaBoxes /> },
      { label: 'Activity Dashboard', path: '/activity', icon: <FaChartBar /> },
    ]
  },
  {
    label: 'Communication',
    items: [
      { label: 'Team Chat', path: '/chat', icon: <FaComments /> },
      { label: 'Communication SLAs', path: '/slas', icon: <FaComments /> },
      { label: 'Contacts', path: '/contacts', icon: <FaAddressBook /> },
    ]
  },
  {
    label: 'Admin Tools',
    items: [
      { label: 'User Management', path: '/admin/users', icon: <FaUsersCog />, adminOnly: true },
      { label: 'System Monitoring', path: '/admin/monitoring', icon: <FaServer />, adminOnly: true },
      { label: 'IT Issues Tracker', path: '/admin/bugs', icon: <FaBug />, adminOnly: true },
    ]
  }
];

export default function Navbar() {
  const { user, logout } = useContext(AuthContext)
  const { totalUnreadCount, notificationPermission } = useChat()
  const isStandard = !['admin', 'super'].includes(user?.role)
  const isSuper = user?.role === 'super'

  // only show "Interview Processes" to admin/super
  const filteredHrSubItems = hrSubItems.filter(
    section => !(section.label === 'Interview Processes' && isStandard)
  )

  // Build OKR links dynamically by role
  const okrLinks = isStandard
    ? [
        { label: 'My OKRs', path: '/company-okrs/my-okrs' },
        { label: 'OKRs', path: '/company-okrs' }
      ]
    : [
        { label: 'Company OKRs', path: '/company-okrs/quarter' },
        { label: 'People OKRs', path: '/company-okrs/people' },
        { label: 'Team OKRs', path: '/company-okrs/teams' }
      ]

  // Desktop dropdown state
  const [businessDropdown, setBusinessDropdown] = useState(false)
  const [companyStrategyDropdown, setCompanyStrategyDropdown] = useState(false)
  const [sopDropdown, setSopDropdown] = useState(false)
  const [okrsDropdown, setOkrsDropdown] = useState(false)
  const [hrDropdown, setHrDropdown] = useState(false)
  const [hrJobDescDropdown, setHrJobDescDropdown] = useState(false)
  const [hrInterviewDropdown, setHrInterviewDropdown] = useState(false)

  // Mobile dropdown state
  const [menuOpen, setMenuOpen] = useState(false)
  const [mobileWorkflowsOpen, setMobileWorkflowsOpen] = useState(false)
  const [mobileAutomationOpen, setMobileAutomationOpen] = useState(false)
  const [mobileCompanyStrategyOpen, setMobileCompanyStrategyOpen] = useState(false)
  const [mobileSopOpen, setMobileSopOpen] = useState(false)
  const [mobileOkrsOpen, setMobileOkrsOpen] = useState(false)
  const [mobileHrOpen, setMobileHrOpen] = useState(false)
  const [mobileHrJobDescOpen, setMobileHrJobDescOpen] = useState(false)
  const [mobileHrInterviewOpen, setMobileHrInterviewOpen] = useState(false)
  const [supportOpen, setSupportOpen] = useState(false);

  const toggleMenu = () => setMenuOpen(v => !v)
  const closeMenu = () => setMenuOpen(false)

  const handleCostCardsPrefetch = () => {
    prefetchCostCardsList()
  }

  // Handle click outside mobile menu
  const handleOverlayClick = (e) => {
    if (e.target.classList.contains('mobile-menu-overlay')) {
      closeMenu()
    }
  }

  return (
    <header className="navbar">
      <div className="navbar-content">
        <div className="navbar-logo">
          <NavLink to="/" onClick={closeMenu}>
            <img src={logo} alt="NESIM Logo" />
            <span className="logo-text">NESIM Intranet</span>
          </NavLink>
        </div>

        {/* Desktop Navigation */}
        <nav className="navbar-links-desktop">
          {sidebarSections.map(section => (
            <div key={section.label} className="sidebar-section">
              <div className="sidebar-section-label">{section.label}</div>
              {section.items.map(item => {
                if (item.adminOnly && user?.role !== 'super') return null;
                
                // Hide Cost Cards link for non-super users
                const canViewCostCards =
                  isSuper ||
                  ['Finance', 'Support'].includes(user?.department)
                if (item.label === 'Cost Cards' && !canViewCostCards) return null;

                if (item.subItems || item.label === 'OKRs') {
                  switch (item.label) {
                    case 'Business Workflows':
                      return (
                        <div key="workflows" className="navbar-item">
                          <button
                            className="navbar-link dropdown-toggle"
                            onClick={e => {
                              e.preventDefault()
                              setBusinessDropdown(b => !b)
                            }}
                          >
                            {item.icon} {item.label}
                            <span className="chevron-icon">
                              {businessDropdown ? <FaChevronDown /> : <FaChevronRight />}
                            </span>
                          </button>
                          {businessDropdown && (
                            <div className="sub-nav open">
                              {item.subItems.map(sub => (
                                <NavLink
                                  key={sub.path}
                                  to={sub.path}
                                  className={({ isActive }) =>
                                    'navbar-link' + (isActive ? ' active' : '')
                                  }
                                  onClick={closeMenu}
                                >
                                  {sub.label}
                                </NavLink>
                              ))}
                            </div>
                          )}
                        </div>
                      )


                    case 'Company Strategy':
                      return (
                        <div key="strategy" className="navbar-item">
                          <button
                            className="navbar-link dropdown-toggle"
                            onClick={e => {
                              e.preventDefault()
                              setCompanyStrategyDropdown(d => !d)
                            }}
                          >
                            {item.icon} {item.label}
                            <span className="chevron-icon">
                              {companyStrategyDropdown ? <FaChevronDown /> : <FaChevronRight />}
                            </span>
                          </button>
                          {companyStrategyDropdown && (
                            <div className="sub-nav open">
                              {item.subItems.map(sub => (
                                <NavLink
                                  key={sub.path}
                                  to={sub.path}
                                  className={({ isActive }) =>
                                    'navbar-link' + (isActive ? ' active' : '')
                                  }
                                  onClick={closeMenu}
                                >
                                  {sub.label}
                                </NavLink>
                              ))}
                            </div>
                          )}
                        </div>
                      )

                    case 'Standard Operating Procedures':
                      return (
                        <div key="sops" className="navbar-item">
                          <button
                            className="navbar-link dropdown-toggle"
                            onClick={e => {
                              e.preventDefault()
                              setSopDropdown(s => !s)
                            }}
                          >
                            {item.icon} {item.label}
                            <span className="chevron-icon">
                              {sopDropdown ? <FaChevronDown /> : <FaChevronRight />}
                            </span>
                          </button>
                          {sopDropdown && (
                            <div className="sub-nav open">
                              {item.subItems.map(sub => (
                                <NavLink
                                  key={sub.path}
                                  to={sub.path}
                                  className={({ isActive }) =>
                                    'navbar-link' + (isActive ? ' active' : '')
                                  }
                                  onClick={closeMenu}
                                >
                                  {sub.label}
                                </NavLink>
                              ))}
                            </div>
                          )}
                        </div>
                      )

                    case 'OKRs':
                      return (
                        <div key="company-okrs" className="navbar-item">
                          <button
                            className="navbar-link dropdown-toggle"
                            onClick={e => {
                              e.preventDefault()
                              setOkrsDropdown(o => !o)
                            }}
                          >
                            {item.icon} {item.label}
                            <span className="chevron-icon">
                              {okrsDropdown ? <FaChevronDown /> : <FaChevronRight />}
                            </span>
                          </button>
                          {okrsDropdown && (
                            <div className="sub-nav open">
                              {okrLinks.map(link => (
                                <NavLink
                                  key={link.path}
                                  to={link.path}
                                  end
                                  className={({ isActive }) =>
                                    'navbar-link' + (isActive ? ' active' : '')
                                  }
                                  onClick={closeMenu}
                                >
                                  {link.label}
                                </NavLink>
                              ))}
                            </div>
                          )}
                        </div>
                      )

                    case 'Human Resources':
                      return (
                        <div key="hr" className="navbar-item">
                          <button
                            className="navbar-link dropdown-toggle"
                            onClick={e => {
                              e.preventDefault()
                              setHrDropdown(h => !h)
                            }}
                          >
                            {item.icon} {item.label}
                            <span className="chevron-icon">
                              {hrDropdown ? <FaChevronDown /> : <FaChevronRight />}
                            </span>
                          </button>
                          {hrDropdown && (
                            <div className="sub-nav open">
                              {filteredHrSubItems.map(hrItem => {
                                if (hrItem.subItems) {
                                  const isJob =
                                    hrItem.label === 'Job Descriptions and Levels'
                                  const open = isJob
                                    ? hrJobDescDropdown
                                    : hrInterviewDropdown
                                  const toggle = () =>
                                    isJob
                                      ? setHrJobDescDropdown(j => !j)
                                      : setHrInterviewDropdown(i => !i)

                                  return (
                                    <div
                                      key={hrItem.label}
                                      className="navbar-item nested"
                                    >
                                      <button
                                        className="navbar-link dropdown-toggle"
                                        onClick={e => {
                                          e.preventDefault()
                                          toggle()
                                        }}
                                      >
                                        {hrItem.label}
                                        <span className="chevron-icon">
                                          {open ? <FaChevronDown /> : <FaChevronRight />}
                                        </span>
                                      </button>
                                      {open && (
                                        <div className="sub-nav open">
                                          {hrItem.subItems.map(sub => (
                                            <NavLink
                                              key={sub.path}
                                              to={sub.path}
                                              className={({ isActive }) =>
                                                'navbar-link' + (isActive ? ' active' : '')
                                              }
                                              onClick={closeMenu}
                                            >
                                              {sub.label}
                                            </NavLink>
                                          ))}
                                        </div>
                                      )}
                                    </div>
                                  )
                                }
                                return (
                                  <NavLink
                                    key={hrItem.path}
                                    to={hrItem.path}
                                    className={({ isActive }) =>
                                      'navbar-link' + (isActive ? ' active' : '')
                                    }
                                    onClick={closeMenu}
                                  >
                                    {hrItem.label}
                                  </NavLink>
                                )
                              })}
                            </div>
                          )}
                        </div>
                      )

                    default:
                      return null
                  }
                } else {
                  return (
                    <NavLink
                      key={item.path}
                      to={item.path}
                      className={({ isActive }) =>
                        'navbar-link' + (isActive ? ' active' : '')
                      }
                      onClick={closeMenu}
                      onMouseEnter={item.label === 'Cost Cards' ? handleCostCardsPrefetch : undefined}
                    >
                      {item.icon} {item.label}
                      {item.label === 'Team Chat' && totalUnreadCount > 0 && (
                        <span className="navbar-unread-badge">{totalUnreadCount}</span>
                      )}
                      {item.label === 'Team Chat' && !notificationPermission && (
                        <span className="notification-disabled-indicator" title="Browser notifications are disabled. Enable them to get notified of new messages.">
                          🔕
                        </span>
                      )}
                    </NavLink>
                  )
                }
              })}
            </div>
          ))}
        </nav>

        {/* Mobile Hamburger */}
        <div className="hamburger" onClick={toggleMenu}>
          <span className="bar" />
          <span className="bar" />
          <span className="bar" />
        </div>
      </div>

      {/* Mobile Overlay */}
      {menuOpen && (
        <div className="mobile-menu-overlay" onClick={handleOverlayClick}>
          <div className="mobile-nav-links" onClick={e => e.stopPropagation()}>
            {/* Enhanced User Profile Section */}
            <div className="mobile-user-profile">
              <button 
                className="mobile-close-button" 
                onClick={closeMenu}
                title="Close menu"
              >
                ×
              </button>
              <div className="mobile-user-avatar">
                <FaUserCircle />
              </div>
              <h3 className="mobile-user-name">{user?.name || 'User'}</h3>
              <p className="mobile-user-role">{user?.role === 'super' ? 'Super Admin' : user?.role === 'admin' ? 'Administrator' : 'User'}</p>
              <NavLink to="/settings" className="mobile-user-settings">
                <FaCog /> Settings
              </NavLink>
              <NavLink to="/my-support-tickets" className="mobile-user-settings">
                <FaBug /> My Support Tickets
              </NavLink>
            </div>
            
            <nav className="mobile-nav">
              {sidebarSections.map(section => (
                <div key={section.label} className="mobile-section">
                  <div className="mobile-section-header">{section.label}</div>
                  {section.items.map(item => {
                    if (item.adminOnly && user?.role !== 'super') return null;
                    if (item.label === 'Cost Cards' && !isSuper) return null;

                    if (!item.subItems && item.label !== 'OKRs') {
                      return (
                        <NavLink
                          key={item.path}
                          to={item.path}
                          className={({ isActive }) =>
                            'mobile-nav-link' + (isActive ? ' active' : '')
                          }
                          onClick={closeMenu}
                          onTouchStart={item.label === 'Cost Cards' ? handleCostCardsPrefetch : undefined}
                        >
                          {item.icon} {item.label}
                          {item.label === 'Team Chat' && totalUnreadCount > 0 && (
                            <span className="mobile-nav-unread-badge">{totalUnreadCount}</span>
                          )}
                          {item.label === 'Team Chat' && !notificationPermission && (
                            <span className="mobile-notification-disabled-indicator" title="Browser notifications are disabled. Enable them to get notified of new messages.">
                              🔕
                            </span>
                          )}
                        </NavLink>
                      )
                    }

                    if (item.label === 'Business Workflows') {
                      return (
                        <div key="mobile-workflows" className="mobile-nav-item">
                          <button
                            className="mobile-nav-link"
                            onClick={() => setMobileWorkflowsOpen(v => !v)}
                          >
                            {item.icon} {item.label}
                            <span className="chevron-icon">
                              {mobileWorkflowsOpen ? <FaChevronDown /> : <FaChevronRight />}
                            </span>
                          </button>
                          {mobileWorkflowsOpen && (
                            <div className="mobile-sub-nav open">
                              {workflowsSubItems.map(sub => (
                                <NavLink
                                  key={sub.path}
                                  to={sub.path}
                                  className={({ isActive }) =>
                                    'mobile-nav-link' + (isActive ? ' active' : '')
                                  }
                                  onClick={closeMenu}
                                >
                                  {sub.label}
                                </NavLink>
                              ))}
                            </div>
                          )}
                        </div>
                      )
                    }

                    if (item.label === 'Automation') {
                      return (
                        <div key="mobile-automation" className="mobile-nav-item">
                          <button
                            className="mobile-nav-link"
                            onClick={() => setMobileAutomationOpen(v => !v)}
                          >
                            {item.icon} {item.label}
                            <span className="chevron-icon">
                              {mobileAutomationOpen ? <FaChevronDown /> : <FaChevronRight />}
                            </span>
                          </button>
                          {mobileAutomationOpen && (
                            <div className="mobile-sub-nav open">
                              {automationSubItems.map(sub => (
                                <NavLink
                                  key={sub.path}
                                  to={sub.path}
                                  className={({ isActive }) =>
                                    'mobile-nav-link' + (isActive ? ' active' : '')
                                  }
                                  onClick={closeMenu}
                                >
                                  {sub.label}
                                </NavLink>
                              ))}
                            </div>
                          )}
                        </div>
                      )
                    }

                    if (item.label === 'Company Strategy') {
                      return (
                        <div key="mobile-strategy" className="mobile-nav-item">
                          <button
                            className="mobile-nav-link"
                            onClick={() => setMobileCompanyStrategyOpen(v => !v)}
                          >
                            {item.icon} {item.label}
                            <span className="chevron-icon">
                              {mobileCompanyStrategyOpen ? <FaChevronDown /> : <FaChevronRight />}
                            </span>
                          </button>
                          {mobileCompanyStrategyOpen && (
                            <div className="mobile-sub-nav open">
                              {companyStrategySubItems.map(sub => (
                                <NavLink
                                  key={sub.path}
                                  to={sub.path}
                                  className={({ isActive }) =>
                                    'mobile-nav-link' + (isActive ? ' active' : '')
                                  }
                                  onClick={closeMenu}
                                >
                                  {sub.label}
                                </NavLink>
                              ))}
                            </div>
                          )}
                        </div>
                      )
                    }

                    if (item.label === 'Standard Operating Procedures') {
                      return (
                        <div key="mobile-sops" className="mobile-nav-item">
                          <button
                            className="mobile-nav-link"
                            onClick={() => setMobileSopOpen(v => !v)}
                          >
                            {item.icon} {item.label}
                            <span className="chevron-icon">
                              {mobileSopOpen ? <FaChevronDown /> : <FaChevronRight />}
                            </span>
                          </button>
                          {mobileSopOpen && (
                            <div className="mobile-sub-nav open">
                              {sopSubItems.map(sub => (
                                <NavLink
                                  key={sub.path}
                                  to={sub.path}
                                  className={({ isActive }) =>
                                    'mobile-nav-link' + (isActive ? ' active' : '')
                                  }
                                  onClick={closeMenu}
                                >
                                  {sub.label}
                                </NavLink>
                              ))}
                            </div>
                          )}
                        </div>
                      )
                    }

                    if (item.label === 'OKRs') {
                      return (
                        <div key="mobile-okrs" className="mobile-nav-item">
                          <button
                            className="mobile-nav-link"
                            onClick={() => setMobileOkrsOpen(v => !v)}
                          >
                            {item.icon} {item.label}
                            <span className="chevron-icon">
                              {mobileOkrsOpen ? <FaChevronDown /> : <FaChevronRight />}
                            </span>
                          </button>
                          {mobileOkrsOpen && (
                            <div className="mobile-sub-nav open">
                              {okrLinks.map(link => (
                                <NavLink
                                  key={link.path}
                                  to={link.path}
                                  end
                                  className={({ isActive }) =>
                                    'mobile-nav-link' + (isActive ? ' active' : '')
                                  }
                                  onClick={closeMenu}
                                >
                                  {link.label}
                                </NavLink>
                              ))}
                            </div>
                          )}
                        </div>
                      )
                    }

                    if (item.label === 'Human Resources') {
                      return (
                        <div key="mobile-hr" className="mobile-nav-item">
                          <button
                            className="mobile-nav-link"
                            onClick={() => setMobileHrOpen(v => !v)}
                          >
                            {item.icon} {item.label}
                            <span className="chevron-icon">
                              {mobileHrOpen ? <FaChevronDown /> : <FaChevronRight />}
                            </span>  
                          </button>
                          {mobileHrOpen && (
                            <div className="mobile-sub-nav open">
                              {filteredHrSubItems.map(hrItem => {
                                if (hrItem.subItems) {
                                  const isJob =
                                    hrItem.label === 'Job Descriptions and Levels'
                                  const open = isJob
                                    ? mobileHrJobDescOpen
                                    : mobileHrInterviewOpen
                                  const toggle = () =>
                                    isJob
                                      ? setMobileHrJobDescOpen(j => !j)
                                      : setMobileHrInterviewOpen(i => !i)

                                  return (
                                    <div
                                      key={hrItem.label}
                                      className="mobile-nav-item nested"
                                    >
                                      <button
                                        className="mobile-nav-link"
                                        onClick={() => toggle()}
                                      >
                                        {hrItem.label}
                                        <span className="chevron-icon">
                                          {open ? <FaChevronDown /> : <FaChevronRight />}
                                        </span>
                                      </button>
                                      {open && (
                                        <div className="mobile-sub-nav open">
                                          {hrItem.subItems.map(sub => (
                                            <NavLink
                                              key={sub.path}
                                              to={sub.path}
                                              className={({ isActive }) =>
                                                'mobile-nav-link' + (isActive ? ' active' : '')
                                              }
                                              onClick={closeMenu}
                                            >
                                              {sub.label}
                                            </NavLink>
                                          ))}
                                        </div>
                                      )}
                                    </div>
                                  )
                                }
                                return (
                                  <NavLink
                                    key={hrItem.path}
                                    to={hrItem.path}
                                    className={({ isActive }) =>
                                      'mobile-nav-link' + (isActive ? ' active' : '')
                                    }
                                    onClick={closeMenu}
                                  >
                                    {hrItem.label}
                                  </NavLink>
                                )
                              })}
                            </div>
                          )}
                        </div>
                      )
                    }

                    return null
                  })}
                </div>
              ))}
            </nav>
            
            {/* Sticky Help & Support button */}
            <div className="mobile-support-section">
              <button
                className="mobile-support-btn"
                onClick={() => setSupportOpen(true)}
              >
                <FaQuestionCircle />
                Help & Support
              </button>
            </div>
            
            {/* Sticky Mobile Logout Section */}
            <div className="mobile-logout-section">
              <button
                className="logout-mobile"
                onClick={() => {
                  logout()
                  closeMenu()
                }}
              >
                <FaSignOutAlt />
                Sign out
              </button>
            </div>
          </div>
          
          <SupportFormModal
            isOpen={supportOpen}
            onClose={() => setSupportOpen(false)}
            onSubmit={async (data) => {
              try {
                const res = await import('../utils/api').then(m => m.submitSupportForm(data));
                if (!res.success) throw new Error('Failed to submit');
                return res;
              } catch (err) {
                throw new Error('Failed to submit support ticket.');
              }
            }}
            user={user}
          />
        </div>
      )}
    </header>
  )
}
